import { Invoice, Id, isIdMatch } from '@hero/schema';
import { CONFIG } from '../config';
import { isAnyCarrier, isAnyCustomer } from '../utils';

/**
 * @deprecated — broker-era helper that assumes `super_admin_system`
 * is one of the two parties on every invoice. Replaced by
 * {@link getInvoiceRoleForViewer} for the bilateral model.
 *
 * Kept around while the legacy lists (admin Carrier / Customer
 * invoices tabs) are still wired, so a typescript-friendly fallback
 * exists. Slated for removal once the admin lists collapse to one
 * bilateral list.
 */
export const getInvoiceTargetBeneficiary = (invoice: Invoice) => {
  const superAdminId = CONFIG.superAdmin.beneficiary._id;
  // if seller is super admin, then the target party is the buyer ... vise versa.
  const targetBeneficiary = isIdMatch(invoice.seller._id, superAdminId)
    ? invoice.buyer
    : invoice.seller;

  const isPayer = isIdMatch(targetBeneficiary._id, invoice.buyer._id);

  return {
    /** the target party of the invoice — never the super_admin_system */
    invoiceBeneficiary: targetBeneficiary,
    /** legacy: `true` when the target party is the buyer */
    isPayer,
    isCarrier: isAnyCarrier(targetBeneficiary.target),
    isCustomer: isAnyCustomer(targetBeneficiary.target),
  };
};

/**
 * Bilateral viewer-role helper — replaces
 * {@link getInvoiceTargetBeneficiary} for org-to-org invoices where
 * Hero is no longer a counterparty.
 *
 * Returns the role the `viewerOrgId` plays on the invoice along with
 * the counterparty `Party` snapshot, ready for UI labels, CTA
 * gating ("Finance it now" only for sellers), and tab grouping.
 *
 * - `'buyer'`         → viewerOrgId matches `invoice.buyer._id`
 * - `'seller'`        → viewerOrgId matches `invoice.seller._id`
 * - `'observer'`      → viewerOrgId matches neither side (admin
 *                       super-org, support, audit dashboards)
 *
 * Crucially this does NOT depend on the legacy
 * `super_admin_system` heuristic — `Hero` reading an invoice between
 * two third-party orgs comes out as `'observer'`, which is the
 * correct semantic.
 */
export const getInvoiceRoleForViewer = (
  invoice: Invoice,
  viewerOrgId: Id | string | undefined,
) => {
  if (!viewerOrgId) {
    return {
      role: 'observer' as const,
      viewerParty: undefined,
      counterparty: invoice.seller, // arbitrary default — UI should not branch on this when role==='observer'
    };
  }

  if (isIdMatch(invoice.buyer._id, viewerOrgId)) {
    return {
      role: 'buyer' as const,
      viewerParty: invoice.buyer,
      counterparty: invoice.seller,
    };
  }

  if (isIdMatch(invoice.seller._id, viewerOrgId)) {
    return {
      role: 'seller' as const,
      viewerParty: invoice.seller,
      counterparty: invoice.buyer,
    };
  }

  return {
    role: 'observer' as const,
    viewerParty: undefined,
    counterparty: invoice.seller,
  };
};

export type InvoiceViewerRole = ReturnType<typeof getInvoiceRoleForViewer>['role'];
